import { type VariantProps, cv } from "@suse-o11y/class-variance";
import { IconCaretDown } from "@suse-o11y/icons";
import { type IgnorePasswordManagers, ignorePasswordManagerAttributes } from "@suse-o11y/ignore-password-managers";
import { mergeRefs } from "@suse-o11y/merge-refs";
import { useComputed } from "@suse-o11y/signal";
import { useOnInvalidFormFocus } from "@suse-o11y/signal/form";
import { Slot, SlotHost } from "@suse-o11y/slot";
import React from "react";

import { useFormContext } from "~/components/form/use-form-context";
import { useFormFieldContext } from "~/components/form/use-form-field-context";

import styles from "./_select.module.css";

const LazyTrigger = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Trigger } = await import("@radix-ui/react-select");

	return { default: Trigger };
});

const LazyIcon = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Icon } = await import("@radix-ui/react-select");

	return { default: Icon };
});

/**
 * Check out Figma for the design of the select:
 *
 * @link https://www.figma.com/design/YIkZms7ygvgqXYnbzDFr8Y/--StackState-DS?node-id=4348-1220&t=ciwqpzgoL6DiywW9-4
 */
const variants = cv(styles.trigger, {
	variants: {
		variant: {
			neutral: styles.neutral,
			error: styles.error,
		},
		size: {
			sm: styles.sm,
			md: styles.md,
			lg: styles.lg,
		},
	},
	defaultVariants: {
		size: "md",
		variant: "neutral",
	},
});

export interface SelectTriggerProps
	extends IgnorePasswordManagers,
		VariantProps<typeof variants>,
		React.ComponentPropsWithoutRef<typeof LazyTrigger> {
	loading?: boolean;
}

export const SelectTrigger = React.forwardRef<React.ElementRef<typeof LazyTrigger>, SelectTriggerProps>(
	(
		{ asChild = false, allowPasswordManager, variant, size, loading, className, onBlur, children, disabled, ...props },
		ref,
	) => {
		const Root = asChild ? Slot : "button";
		const form = useFormContext();
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus(formField?.state);
		const submitting = useComputed((monitor) => formField?.state.isSubmitting(monitor), [formField?.state]);
		const invalid = useComputed((monitor) => formField?.state.isInvalid(monitor), [formField?.state]);

		const combinedRef = mergeRefs([ref, focusRef]);
		const classNames = variants({ variant: invalid ? "error" : variant, size, className });

		return (
			<React.Suspense
				fallback={
					<Root
						aria-busy={!disabled || submitting || loading}
						aria-disabled={disabled}
						className={classNames}
						form={form?.id}
						id={formField?.id}
						ref={combinedRef}
						{...props}
					>
						<SlotHost>{children}</SlotHost>
						<TriggerIcon />
					</Root>
				}
			>
				<LazyTrigger
					aria-busy={submitting || loading}
					aria-disabled={disabled}
					asChild
					className={classNames}
					form={form?.id}
					id={formField?.id}
					onBlur={(event) => {
						formField?.state.setTouched(true);
						onBlur?.(event);
					}}
					ref={combinedRef}
					{...ignorePasswordManagerAttributes(allowPasswordManager)}
					{...props}
				>
					<Root>
						<SlotHost>{children}</SlotHost>

						<LazyIcon asChild>
							<TriggerIcon />
						</LazyIcon>
					</Root>
				</LazyTrigger>
			</React.Suspense>
		);
	},
);

const TriggerIcon: React.FC = () => (
	<span className="ml-auto">
		<IconCaretDown className={styles.icon} height={6} width={9} />
	</span>
);

if (import.meta.env.DEV) {
	SelectTrigger.displayName = "SelectTrigger";
}
